/**
 * Hook de acceso a APIs del flujo de reserva.
 *
 * Aísla todas las llamadas HTTP. Si mañana cambia el endpoint, este es el
 * único archivo a tocar. Los componentes solo ven funciones bien nombradas
 * y los datos ya parseados.
 */

import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getApi } from 'views/services/api';

export const useReservationApi = (hotelId) => {
  const [roomData, setRoomData] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [stayTypes, setStayTypes] = useState([]);
  const [existingCustomerData, setExistingCustomerData] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // -- Habitaciones --------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const fetchRooms = async () => {
      try {
        setIsSearching(true);
        const response = await getApi(`api/room/viewallrooms/${hotelId}`);
        if (!cancelled) setRoomData(response?.data || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error cargando habitaciones', err);
        if (!cancelled) toast.error('No se pudieron cargar los datos de la habitación');
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    };

    if (hotelId) fetchRooms();
    return () => {
      cancelled = true;
    };
  }, [hotelId]);

  // -- Tasas de cambio activas --------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const fetchExchangeRates = async () => {
      try {
        const response = await getApi(`api/exchange/viewall/${hotelId}`);
        const activeRates = (response?.data || []).filter((rate) => rate.isActive);
        if (!cancelled) setExchangeRates(activeRates);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error al obtener tasas de cambio:', err);
        if (!cancelled) toast.error('No se pudieron cargar las tasas de cambio');
      }
    };

    if (hotelId) fetchExchangeRates();
    return () => {
      cancelled = true;
    };
  }, [hotelId]);

  // -- Tipos de estancia configurados por el hotel ------------------------
  useEffect(() => {
    let cancelled = false;

    const fetchHotelStayTypes = async () => {
      try {
        const response = await getApi(`api/hotel/stay-types/list/${hotelId}`);
        if (response?.status === 200 && !cancelled) {
          setStayTypes(response.data || []);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch hotel settings', err);
      }
    };

    if (hotelId) fetchHotelStayTypes();
    return () => {
      cancelled = true;
    };
  }, [hotelId]);

  // -- Búsqueda de cliente por teléfono -----------------------------------
  const fetchCustomerData = async (phoneNumber) => {
    try {
      setIsSearching(true);
      const response = await getApi(`api/customer/view/${phoneNumber}?hotelId=${hotelId}`);
      const customer = response?.data?.customerData?.[0];
      setExistingCustomerData(customer || []);
      return customer;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error al obtener datos del cliente', err);
      toast.error('No se pudieron obtener los datos del cliente');
      return undefined;
    } finally {
      setIsSearching(false);
    }
  };

  const resetExistingCustomerData = () => setExistingCustomerData([]);

  return {
    roomData,
    exchangeRates,
    stayTypes,
    existingCustomerData,
    isSearching,
    setIsSearching,
    fetchCustomerData,
    resetExistingCustomerData
  };
};
